import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { ApiErrorResponse, ApiServiceInterface } from '@hutsix/ngxh6';
import { PermitClanAreaSelection, PermitDetail } from '../../../models/permit-detail.model';
import { isViewableImage, uploadedDocumentUrl } from '../uploaded-document-url';

/**
 * Fishing / Hunting detail - common fields plus term, the document, and the clan
 * areas with their per-area approval state.
 *
 * Each area is a separate consultation with that clan's delegates, so staff mark them
 * off one at a time as answers come back; the permit itself can't be approved until
 * every area has one. `canDecide` gates the controls: the CRM passes true, the public
 * magic-link view leaves it false and receives an already-filtered list.
 */
@Component({
    selector: 'app-fishing-hunting-detail',
    templateUrl: './fishing-hunting-detail.component.html',
    styleUrls: ['./fishing-hunting-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FishingHuntingDetailComponent {
    @Input() permit!: PermitDetail;

    /** Served through the backend, not straight off the bucket - see the helper. */
    public readonly documentUrl = uploadedDocumentUrl;
    /** Photos render on the page; anything else stays a link. */
    public readonly isImage = isViewableImage;
    /** Staff-only: show the per-area approve/reject controls. */
    @Input() canDecide = false;
    /** Fires after a decision is saved, so the page can reload the permit. */
    @Output() decided = new EventEmitter<void>();

    /** The area being decided, or 'remaining' when approving everything still pending. */
    public deciding?: PermitClanAreaSelection | 'remaining' | null;
    public decision: 'approved' | 'rejected' = 'approved';
    public staffNote = '';
    public applicantNote = '';
    public visibleToApplicant = true;
    public saving = false;
    public error?: string | null;

    /**
     * What a piece of country is called for this permit's activity.
     *
     * Fishing selects river-system zones, hunting selects clan areas. One collection
     * underneath - the CRM just has to call it what the applicant was shown.
     */
    public get areaWord(): string {
        return this.permit.type === 'fishing' ? 'zone' : 'hunting area';
    }

    public get areaWordPlural(): string {
        return this.permit.type === 'fishing' ? 'Zones' : 'Hunting areas';
    }

    public get pendingCount(): number {
        return (this.permit.fishingHunting?.clanAreaSelections || []).filter(a => a.status === 'pending').length;
    }

    /** Decided areas whose result the applicant still sees as "Pending". */
    public get withheldCount(): number {
        return this.permit.fishingHunting?.withheldDecisionCount || 0;
    }

    public get decidingLabel(): string {
        if (this.deciding === 'remaining') return `Approve the ${this.pendingCount} remaining ${this.areaWord}(s)`;
        if (!this.deciding) return '';
        // Re-opening a decided area without flipping it = just editing the notes.
        if (this.deciding.status === this.decision) return `Edit note for ${this.deciding.clanArea}`;
        const verb = this.deciding.status === 'pending' ? (this.decision === 'approved' ? 'Approve' : 'Reject') : 'Change to ' + this.decision + ':';
        return `${verb} ${this.deciding.clanArea}`;
    }

    public start(selection: PermitClanAreaSelection, decision: 'approved' | 'rejected'): void {
        this.deciding = selection;
        this.decision = decision;
        this.staffNote = selection.staffNote || '';
        this.applicantNote = selection.applicantNote || '';
        this.visibleToApplicant = selection.visibleToApplicant !== false;
        this.error = null;
    }

    /** Re-open a decided area's notes without changing which way it went. */
    public editNote(selection: PermitClanAreaSelection): void {
        this.start(selection, selection.status === 'rejected' ? 'rejected' : 'approved');
    }

    /** Approve every area still awaiting a decision - the usual "they all said yes" case. */
    public startApproveRemaining(): void {
        this.deciding = 'remaining';
        this.decision = 'approved';
        this.staffNote = '';
        this.applicantNote = '';
        this.visibleToApplicant = true;
        this.error = null;
    }

    public cancel(): void {
        this.deciding = null;
        this.error = null;
    }

    public save(): void {
        if (!this.deciding) return;
        const url =
            this.deciding === 'remaining'
                ? `/api/permit/${this.permit.id}/clan-area/approve-remaining`
                : `/api/permit/${this.permit.id}/clan-area/${this.deciding.id}/${this.decision}`;

        this.saving = true;
        this.error = null;
        this.api
            .post({
                url,
                data: {
                    staffNote: this.staffNote || null,
                    applicantNote: this.applicantNote || null,
                    visibleToApplicant: this.visibleToApplicant,
                },
                offline: false,
                displayErrors: false,
            })
            .subscribe(
                () => {
                    this.saving = false;
                    this.deciding = null;
                    this.decided.emit();
                },
                (err: ApiErrorResponse<any>) => {
                    this.saving = false;
                    this.error = err.error?.error?.error || err.message || 'Could not save that decision.';
                    this.cdRef.detectChanges();
                },
            );
    }

    /** Show or hide one area's result without re-deciding it. */
    public toggleVisibility(area: PermitClanAreaSelection): void {
        this.post(`/api/permit/${this.permit.id}/clan-area/${area.id}/visibility`, {
            visibleToApplicant: area.visibleToApplicant === false,
        });
    }

    /** Release every withheld result at once. */
    public releaseAll(): void {
        this.post(`/api/permit/${this.permit.id}/clan-area/release-all`, {});
    }

    private post(url: string, data: Record<string, unknown>): void {
        this.saving = true;
        this.error = null;
        this.api.post({ url, data, offline: false, displayErrors: false }).subscribe(
            () => {
                this.saving = false;
                this.decided.emit();
            },
            (err: ApiErrorResponse<any>) => {
                this.saving = false;
                this.error = err.error?.error?.error || err.message || 'Could not update the applicant’s view.';
                this.cdRef.detectChanges();
            },
        );
    }

    public badgeClass(status: string): string {
        if ('approved' === status) return 'bg-success';
        if ('rejected' === status) return 'bg-danger';
        return 'bg-secondary';
    }

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        private cdRef: ChangeDetectorRef,
    ) {}
}
